import { useState } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  Phone,
  MessageCircle,
  Edit2,
  Mail,
  Camera,
  Star,
  Store,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import api from '../../lib/api';
import { useTeamOverview } from '../../hooks/use-team-overview';
import StatusBadge from '../../components/supervisor/StatusBadge';
import VisitTimeline from '../../components/supervisor/VisitTimeline';
import PhotoThumbnails from '../../components/supervisor/PhotoThumbnails';
import EditPromoterSheet from '../../components/supervisor/EditPromoterSheet';

interface VisitItem {
  visit_id: string;
  store_id: string;
  store_name: string;
  status: string;
  perfect_store_score: number | null;
  check_in_at: string;
  check_out_at: string | null;
  photo_count: number;
}

export default function TeamMemberDetail({ promoterId }: { promoterId: string }) {
  const [, setLocation] = useLocation();
  const [editOpen, setEditOpen] = useState(false);
  const [expandedVisit, setExpandedVisit] = useState<string | null>(null);

  // Get promoter from team overview cache
  const { data: teamData, refetch: refetchTeam } = useTeamOverview();
  const promoter = teamData?.promoters.find((p) => p.promoter_id === promoterId);

  // Fetch today's visits for this promoter
  const today = new Date().toISOString().slice(0, 10);
  const {
    data: visits,
    isLoading: visitsLoading,
  } = useQuery<VisitItem[]>({
    queryKey: ['supervisor', 'promoter-visits', promoterId, today],
    queryFn: async () => {
      const res = await api.get('/visits', {
        params: {
          promoter_id: promoterId,
          date_from: today,
          date_to: today,
        },
      });
      const data = res.data.data;
      // API may return { visits: [...] } or array directly
      return Array.isArray(data) ? data : data.visits || [];
    },
    staleTime: 30000,
  });

  // Loading state
  if (!promoter) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white px-4 pt-safe pb-4 border-b border-gray-100">
          <button onClick={() => setLocation('/supervisor')} className="flex items-center gap-1 text-gray-500 active:text-gray-800 py-2">
            <ChevronLeft size={20} />
            <span className="text-sm">Voltar</span>
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="h-20 bg-gray-200 rounded-xl animate-pulse" />
          <div className="flex gap-3">
            {[1, 2, 3].map((i) => <div key={i} className="flex-1 h-20 bg-gray-200 rounded-xl animate-pulse" />)}
          </div>
          {[1, 2].map((i) => <div key={i} className="h-24 bg-gray-200 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const { name, status, route_progress, today_stats, phone, email } = promoter;
  const totalPhotos = today_stats.total_photos;
  const avgScore = today_stats.avg_score;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white px-4 pt-safe pb-4 border-b border-gray-100">
        <button onClick={() => setLocation('/supervisor')} className="flex items-center gap-1 text-gray-500 active:text-gray-800 py-2 -ml-1">
          <ChevronLeft size={20} />
          <span className="text-sm">Voltar</span>
        </button>
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-base">
              {name.charAt(0)}
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{name}</h1>
              {today_stats.first_checkin && (
                <p className="text-xs text-gray-400">
                  Iniciou {new Date(today_stats.first_checkin).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>
          <StatusBadge status={status} />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Route progress summary */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <Store size={16} className="mx-auto text-primary mb-1" />
            <p className="text-lg font-bold text-gray-900">
              {route_progress.visited}/{route_progress.total}
            </p>
            <p className="text-[10px] text-gray-500 font-medium">Lojas</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <Star size={16} className="mx-auto text-amber-500 mb-1" />
            <p className="text-lg font-bold text-gray-900">
              {avgScore !== null ? `${Number(avgScore).toFixed(0)}%` : '--'}
            </p>
            <p className="text-[10px] text-gray-500 font-medium">Score</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <Camera size={16} className="mx-auto text-blue-500 mb-1" />
            <p className="text-lg font-bold text-gray-900">{totalPhotos}</p>
            <p className="text-[10px] text-gray-500 font-medium">Fotos</p>
          </div>
        </div>

        {/* Visit Timeline */}
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Visitas hoje</h2>
          {visitsLoading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => <div key={i} className="h-20 bg-gray-200 rounded-xl animate-pulse" />)}
            </div>
          ) : (
            <>
              <VisitTimeline visits={visits || []} />

              {/* Expandable photo sections per visit */}
              {visits && visits.length > 0 && (
                <div className="mt-3 space-y-2">
                  {visits
                    .filter((v) => v.photo_count > 0)
                    .map((visit) => (
                      <div key={visit.visit_id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                        <button
                          onClick={() =>
                            setExpandedVisit(expandedVisit === visit.visit_id ? null : visit.visit_id)
                          }
                          className="w-full flex items-center justify-between p-3 active:bg-gray-50 transition-colors"
                        >
                          <span className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                            <Camera size={13} className="text-gray-400" />
                            Fotos - {visit.store_name}
                            <span className="text-gray-400">({visit.photo_count})</span>
                          </span>
                          {expandedVisit === visit.visit_id ? (
                            <ChevronUp size={16} className="text-gray-400" />
                          ) : (
                            <ChevronDown size={16} className="text-gray-400" />
                          )}
                        </button>
                        {expandedVisit === visit.visit_id && (
                          <div className="px-3 pb-3">
                            <PhotoThumbnails visitId={visit.visit_id} />
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Contact info */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">Contato</h2>
            <button
              onClick={() => setEditOpen(true)}
              className="flex items-center gap-1 text-xs text-primary font-medium active:opacity-70"
            >
              <Edit2 size={13} />
              Editar
            </button>
          </div>
          <div className="space-y-2">
            {phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone size={14} className="text-gray-400" />
                <span>{phone}</span>
              </div>
            )}
            {email && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail size={14} className="text-gray-400" />
                <span className="truncate">{email}</span>
              </div>
            )}
            {!phone && !email && (
              <p className="text-xs text-gray-400">Sem informações de contato</p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          {phone && (
            <a
              href={`tel:${phone}`}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-green-600 text-white font-medium text-sm active:bg-green-700 transition-colors"
            >
              <Phone size={16} />
              Ligar
            </a>
          )}
          <button
            onClick={() => setLocation('/conversations')}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-white font-medium text-sm active:bg-primary/90 transition-colors"
          >
            <MessageCircle size={16} />
            Mensagem
          </button>
        </div>
      </div>

      {/* Edit bottom sheet */}
      <EditPromoterSheet
        promoter={{
          promoter_id: promoterId,
          name,
          phone: phone || '',
          email: email || '',
        }}
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={() => refetchTeam()}
      />
    </div>
  );
}
